<template>
  <div
    class="leaderboard-user-row"
    :class="{
      'top-one': entry.rank === 1,
      'top-two': entry.rank === 2,
      'top-three': entry.rank === 3,
      'current-user': isCurrentUser
    }"
  >
    <!-- Rank -->
    <div class="rank-cell">
      <div class="rank-badge" :class="rankBadgeClass">
        {{ formatRank(entry.rank) }}
      </div>
    </div>

    <!-- User Info -->
    <router-link
      :to="`/profile/${entry.name || entry.username}`"
      class="user-info-cell user-info-link"
    >
      <div class="user-avatar">
        <i class="fas fa-user-circle"></i>
      </div>
      <div class="user-details">
        <div class="username">{{ entry.name || entry.username }}</div>
        <div class="user-secondary">
          <div v-if="entry.country_codes && entry.country_codes.length > 0" class="user-countries">
            <span
              v-for="code in entry.country_codes.slice(0, 3)"
              :key="code"
              class="country-flag-mini"
              :title="code"
            >
              {{ getCountryFlag(code) }}
            </span>
            <span v-if="entry.country_codes.length > 3" class="more-countries">
              +{{ entry.country_codes.length - 3 }}
            </span>
          </div>
        </div>
      </div>
    </router-link>

    <!-- Score -->
    <div class="score-cell">
      <div class="score-value">
        <i class="fas fa-star score-icon"></i>
        {{ entry.total_score }}
      </div>
    </div>

    <!-- Country (for country leaderboards) -->
    <div v-if="showCountry && entry.country_code" class="country-cell">
      <span class="country-flag">{{ getCountryFlag(entry.country_code) }}</span>
      <span class="country-code">{{ entry.country_code }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeaderboardUserRow',
  props: {
    entry: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: String,
      default: null
    },
    showCountry: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isCurrentUser() {
      return this.currentUserId && this.entry.user_id === this.currentUserId
    },
    rankBadgeClass() {
      if (this.entry.rank === 1) return 'rank-gold'
      if (this.entry.rank === 2) return 'rank-silver'
      if (this.entry.rank === 3) return 'rank-bronze'
      return 'rank-default'
    }
  },
  methods: {
    formatRank(rank) {
      if (rank === 1) return '🥇'
      if (rank === 2) return '🥈'
      if (rank === 3) return '🥉'
      return `#${rank}`
    },
    getCountryFlag(countryCode) {
      if (!countryCode || countryCode.length !== 2) return '🌍'
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt())
      return String.fromCodePoint(...codePoints)
    }
  }
}
</script>

<style scoped>
.leaderboard-user-row {
  display: grid;
  grid-template-columns: 80px 1fr 120px;
  align-items: center;
  padding: 16px 20px;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.leaderboard-user-row:hover {
  background: #f9fafb;
  transform: translateX(4px);
}

/* Top 3 styling */
.top-one {
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  border-left: 4px solid #ffd700;
}

.top-two {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-left: 4px solid #c0c0c0;
}

.top-three {
  background: linear-gradient(135deg, #fff4e6 0%, #ffffff 100%);
  border-left: 4px solid #cd7f32;
}

/* Current user highlight */
.current-user {
  background: linear-gradient(135deg, #ede7f6 0%, #ffffff 100%);
  border-left: 4px solid #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.current-user:hover {
  background: linear-gradient(135deg, #e8e0f5 0%, #f9fafb 100%);
}

/* Rank Cell */
.rank-cell {
  display: flex;
  justify-content: center;
}

.rank-badge {
  font-size: 1.25rem;
  font-weight: bold;
  text-align: center;
  min-width: 50px;
}

.rank-gold {
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
}

.rank-silver {
  color: #c0c0c0;
  text-shadow: 0 0 10px rgba(192, 192, 192, 0.5);
}

.rank-bronze {
  color: #cd7f32;
  text-shadow: 0 0 10px rgba(205, 127, 50, 0.5);
}

.rank-default {
  color: #6b7280;
}

/* User Info Cell */
.user-info-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info-link {
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
}

.user-info-link:hover {
  opacity: 0.8;
}

.user-info-link:hover .username {
  color: #667eea;
  text-decoration: underline;
}

.user-avatar {
  font-size: 2.5rem;
  color: #667eea;
}

.top-one .user-avatar {
  color: #ffd700;
}

.top-two .user-avatar {
  color: #c0c0c0;
}

.top-three .user-avatar {
  color: #cd7f32;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.username {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.user-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.user-handle {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 400;
}

.user-countries {
  display: flex;
  align-items: center;
  gap: 4px;
}

.country-flag-mini {
  font-size: 0.875rem;
}

.more-countries {
  font-size: 0.75rem;
  color: #6b7280;
  margin-left: 2px;
}

/* Score Cell */
.score-cell {
  display: flex;
  justify-content: flex-end;
}

.score-value {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.125rem;
  font-weight: bold;
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  padding: 8px 16px;
  border-radius: 20px;
}

.score-icon {
  color: #fbbf24;
}

/* Country Cell (for country leaderboards) */
.country-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.country-flag {
  font-size: 1.5rem;
}

.country-code {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .leaderboard-user-row {
    grid-template-columns: 60px 1fr 100px;
    padding: 12px 16px;
  }

  .rank-badge {
    font-size: 1rem;
    min-width: 40px;
  }

  .user-avatar {
    font-size: 2rem;
  }

  .username {
    font-size: 0.875rem;
  }

  .user-handle {
    font-size: 0.75rem;
  }

  .score-value {
    font-size: 1rem;
    padding: 6px 12px;
  }

  .country-flag-mini {
    font-size: 0.75rem;
  }

  .more-countries {
    font-size: 0.625rem;
  }
}

@media (max-width: 480px) {
  .leaderboard-user-row {
    grid-template-columns: 50px 1fr 80px;
    padding: 10px 12px;
  }

  .rank-badge {
    font-size: 0.875rem;
    min-width: 35px;
  }

  .user-avatar {
    font-size: 1.75rem;
  }

  .username {
    font-size: 0.8125rem;
  }

  .user-handle {
    display: none;
  }

  .score-value {
    font-size: 0.875rem;
    padding: 4px 10px;
  }

  .user-countries {
    display: none;
  }
}
</style>
